// frontend/src/services/api/modules/objects.js
import { API_URL } from '../config';
import { getAuthHeaders, fetchWrapper } from '../utils/fetchWrapper';

export const obtenerObjetosPorIdUsuario = async (ownerId) => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetchWrapper(
      `${API_URL}/api/objetos/porIdUsuario?uid=${encodeURIComponent(ownerId)}`,
      { headers }
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        error: errorData.error || "Error al obtener objetos por ID de usuario",
      };
    }
    try {
      const jsonData = await response.json();
      return jsonData;
    } catch (jsonError) {
      console.error("Error decayendo JSON de obtenerObjetosPorIdUsuario:", jsonError);
      return { error: "Error al procesar la respuesta del servidor" };
    }
  } catch (networkError) {
    console.error("Error en obtenerObjetosPorIdUsuario:", networkError);
    return { error: "Error de conexión con el servidor" };
  }
};

export const actualizarObjeto = async (id, nuevosDatos) => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetchWrapper(`${API_URL}/api/objetos/actualizar/${id}`, {
      method: "PATCH",
      headers,
      body: JSON.stringify(nuevosDatos),
    });
    return await response.json();
  } catch (err) {
    console.error("Error en actualizarObjeto:", err);
    return { error: "Error de conexión con el servidor" };
  }
};

export const activarRegistro = async (objetoID, datosActivacion) => {
  try {
    const response = await fetchWrapper(`${API_URL}/api/activacion/activar`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ objetoID, datosActivacion }),
    });
    return await response.json();
  } catch (error) {
    console.error("Error activando registro:", error);
    return null;
  }
};

export const obtenerObjetoConUsuarioByCode = async (codigoAcceso) => {
  try {
    const response = await fetchWrapper(
      `${API_URL}/api/objetos/conUsuarioByCode?code=${encodeURIComponent(codigoAcceso)}`
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return { error: errorData.error || "Error al obtener objeto + usuario" };
    }
    return await response.json();
  } catch (error) {
    console.error("Error en obtenerObjetoConUsuarioByCode:", error);
    return { error: "Error interno al llamar conUsuarioByCode." };
  }
};

export const togglePerdido = async (objId) => {
  try {
    const headers = await getAuthHeaders();
    const resp = await fetchWrapper(`${API_URL}/api/objetos/marcarPerdido/${objId}`, {
      method: "PATCH",
      headers,
    });
    if (!resp.ok) {
      const errorData = await resp.json().catch(() => ({}));
      return { error: errorData.error || "Error en toggle perdido" };
    }
    return await resp.json();
  } catch (error) {
    console.error("Error togglePerdido =>", error);
    return { error: "Error de conexión" };
  }
};

export const notificarObjetoExtraviado = async (registro) => {
  try {
    const response = await fetchWrapper(`${API_URL}/api/objetos/consultar/notificar-extraviado`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ registro }),
    });
    return await response.json();
  } catch (error) {
    console.error("Error notificando objeto extraviado:", error);
    return null;
  }
};

export const obtenerRecordatorio = async (objetoId) => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetchWrapper(`${API_URL}/objetos/${objetoId}/recordatorio`, {
      headers
    });
    
    if (!response.ok) throw new Error('Error al obtener recordatorio');
    return await response.json();
  } catch (error) {
    console.error('Error:', error);
    return { error: error.message };
  }
};

export const actualizarRecordatorio = async (objetoId, config) => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetchWrapper(`${API_URL}/api/objetos/${objetoId}/recordatorio`, {
      method: 'PATCH',
      headers,
      body: JSON.stringify(config)
    });

    if (response.status === 401) {
      return { error: 'Token inválido o expirado' };
    }

    const data = await response.json();

    if (!response.ok) {
      return { error: data.error || 'Error al actualizar recordatorio' };
    }

    return data;
  } catch (error) {
    console.error('Error:', error);
    return { error: error.message };
  }
};

// ============================================================
// FUNCIONES DE LIBERACIÓN/VENTA DE TAGS
// ============================================================

/**
 * Libera un tag individual para que pueda ser activado.
 * @param {string} objetoId - ID del objeto a liberar
 * @returns {Promise<Object>} Resultado de la operación
 */
export const liberarTag = async (objetoId) => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetchWrapper(`${API_URL}/api/objetos/${objetoId}/liberar`, {
      method: 'PATCH',
      headers
    });

    const data = await response.json();

    if (!response.ok) {
      return { error: data.error || 'Error al liberar tag' };
    }

    return data;
  } catch (error) {
    console.error('Error en liberarTag:', error);
    return { error: error.message };
  }
};

/**
 * Libera múltiples tags en lote.
 * @param {Array<string>} ids - Array de IDs de objetos a liberar
 * @returns {Promise<Object>} Resultado de la operación
 */
export const liberarTagsLote = async (ids) => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetchWrapper(`${API_URL}/api/objetos/liberar-lote`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ ids })
    });

    const data = await response.json();

    if (!response.ok) {
      return { error: data.error || 'Error al liberar tags en lote' };
    }

    return data;
  } catch (error) {
    console.error('Error en liberarTagsLote:', error);
    return { error: error.message };
  }
};

// ============================================================
// FUNCIONES DE VENTA DE TAGS (Salida de Inventario)
// ============================================================

/**
 * Marca un tag como vendido (salió de inventario físico).
 * @param {string} objetoId - ID del objeto a marcar como vendido
 * @returns {Promise<Object>} Resultado de la operación
 */
export const venderTag = async (objetoId) => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetchWrapper(`${API_URL}/api/objetos/${objetoId}/vender`, {
      method: 'PATCH',
      headers
    });

    const data = await response.json();

    if (!response.ok) {
      return { error: data.error || 'Error al marcar tag como vendido' };
    }

    return data;
  } catch (error) {
    console.error('Error en venderTag:', error);
    return { error: error.message };
  }
};

/**
 * Marca múltiples tags como vendidos en lote.
 * @param {Array<string>} ids - Array de IDs de objetos a marcar como vendidos
 * @returns {Promise<Object>} Resultado de la operación
 */
export const venderTagsLote = async (ids) => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetchWrapper(`${API_URL}/api/objetos/vender-lote`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ ids })
    });

    const data = await response.json();

    if (!response.ok) {
      return { error: data.error || 'Error al marcar tags como vendidos' };
    }

    return data;
  } catch (error) {
    console.error('Error en venderTagsLote:', error);
    return { error: error.message };
  }
};

export const resetearDatosObjeto = async (objetoId, pin, secciones) => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetchWrapper(`${API_URL}/api/objetos/${objetoId}/resetear`, {
      method: 'POST',
      headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify({ pin, secciones })
    });
    const data = await response.json();
    if (!response.ok) return { error: data.error || 'Error al resetear datos' };
    return data;
  } catch (error) {
    return { error: error.message };
  }
};